import { Palette, Theme } from "@mui/material/styles";
import { GetObjectKeysAsTemplateLiterals } from "@simplyk/common";

import {
  LegacyZeffyColors,
  LegacyZeffyCorePalette,
  ZeffyColorTokenBorder,
  ZeffyColorTokens,
  ZeffyColorTokenText,
} from "../../common/types/mui";

import { LegacyTypographyPaletteVibes, LegacyTypographyZeffyColorVibes } from "./types";

export type ZeffyColorTokenTextVibes = GetObjectKeysAsTemplateLiterals<Pick<ZeffyColorTokens, "text">>;
export type ZeffyColorTokenBorderClasses = GetObjectKeysAsTemplateLiterals<Pick<ZeffyColorTokens, "border">>;

export type PrefixedKey<Prefix extends string, Key extends string> = `${Prefix}-${Key}`;

export type InternalClasses<Prefix extends string, Classes extends string> = Record<
  Classes,
  PrefixedKey<Prefix, Classes>
>;

const BORDER_PREFIX = "border-";

export const generateZeffyBorderColorVibes = (theme: Theme): ZeffyColorTokenBorderClasses[] =>
  Object.keys(theme.palette.border)
    .map((token) =>
      typeof theme.palette.border[token as keyof ZeffyColorTokenBorder] === "string"
        ? `${BORDER_PREFIX}${token}`
        : Object.keys(theme.palette.border[token as keyof ZeffyColorTokenBorder]).map(
            (colorShade) => `${BORDER_PREFIX}${token}-${colorShade}` as ZeffyColorTokenBorderClasses
          )
    )
    .flat() as ZeffyColorTokenBorderClasses[];

const TEXT_PREFIX = "text-";

export const generateZeffyTextColorVibes = (theme: Theme): ZeffyColorTokenTextVibes[] =>
  Object.keys(theme.palette.text)
    .map((token) =>
      typeof theme.palette.text[token as keyof ZeffyColorTokenText] === "string"
        ? `${TEXT_PREFIX}${token}`
        : Object.keys(theme.palette.text[token as keyof ZeffyColorTokenText]).map(
            (colorShade) => `${TEXT_PREFIX}${token}-${colorShade}` as ZeffyColorTokenTextVibes
          )
    )
    .flat() as ZeffyColorTokenTextVibes[];

export const getNestedValueFromPalette = (record: Palette, path: string) => {
  const splitPathArray = path.split("-");

  return getNestedValueFromRecord(record as unknown as Record<string, unknown>, splitPathArray);
};

const getNestedValueFromRecord = (
  record: Record<string, unknown> | string,
  currentPath: string[]
): string | undefined => {
  if (typeof record !== "object" || currentPath.length === 0) {
    return record as string | undefined;
  }

  return getNestedValueFromRecord(
    record[currentPath[0]] as Record<string, unknown> | string,
    currentPath.length > 1 ? currentPath.slice(1) : []
  );
};

/**
 * Generates legacy typography vibes for neutral and neutralVariant colors with all their shades
 * @param theme - MUI Theme
 * @returns Array of vibe strings like "neutral-100", "neutral-99", "neutralVariant-100", etc.
 */
export const generateLegacyTypographyZeffyColorVibes = (
  theme: Theme
): Array<LegacyTypographyZeffyColorVibes<LegacyZeffyColors>> => {
  const legacyColors: LegacyZeffyColors[] = ["neutral", "neutralVariant"];

  return legacyColors.flatMap((color) =>
    Object.keys(theme.palette[color]).map(
      (shade) => `${color}-${shade}` as LegacyTypographyZeffyColorVibes<LegacyZeffyColors>
    )
  );
};

/**
 * Generates legacy typography palette vibes for core palette colors
 * @param theme - MUI Theme
 * @returns Array of vibe strings like "primary-main", "primary-light", "secondary-dark", etc.
 */
export const generateLegacyTypographyPaletteVibes = (theme: Theme): LegacyTypographyPaletteVibes[] => {
  const coreColors: LegacyZeffyCorePalette[] = [
    "primary",
    "secondary",
    "tertiary",
    "info",
    "success",
    "error",
    "warning",
  ];

  return coreColors.flatMap((color) =>
    Object.keys(theme.palette[color]).map((key) => `${color}-${key}` as LegacyTypographyPaletteVibes)
  );
};
